import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { bookingAPI } from '../../services/crmAPI';
import CRMSubNav from './CRMSubNav';
import './CRM.css';

interface MeetingType {
  name: string;
  slug: string;
  color: string;
  duration_minutes: number;
}

interface Appointment {
  id: string;
  meeting_type: MeetingType;
  booker_name: string;
  booker_email: string;
  booker_phone: string;
  customer_name: string | null;
  customer_id: number | null;
  date: string;
  start_time: string;
  end_time: string;
  notes: string;
  status: string;
  video_link: string;
  contact_log_id: number | null;
  created_at: string;
}

interface Blockout {
  id: string;
  date: string;
  start_time: string | null;
  end_time: string | null;
  reason: string;
  all_day: boolean;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatTime12(time24: string): string {
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
}

const STATUS_COLORS: Record<string, string> = {
  confirmed: '#2D5A27',
  completed: '#666',
  cancelled: '#ccc',
  no_show: '#8B0000',
};

const CRMCalendar: React.FC = () => {
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  });
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [blockouts, setBlockouts] = useState<Blockout[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [editingAppt, setEditingAppt] = useState<Appointment | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editVideoLink, setEditVideoLink] = useState('');
  const [saving, setSaving] = useState(false);

  const monthStr = `${currentMonth.year}-${currentMonth.month.toString().padStart(2, '0')}`;

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bookingAPI.getCalendar(monthStr);
      setAppointments(res.data.appointments || []);
      setBlockouts(res.data.blockouts || []);
    } catch (err) {
      console.error('Failed to load calendar:', err);
    } finally {
      setLoading(false);
    }
  }, [monthStr]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  const handlePrevMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 1) return { year: prev.year - 1, month: 12 };
      return { year: prev.year, month: prev.month - 1 };
    });
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 12) return { year: prev.year + 1, month: 1 };
      return { year: prev.year, month: prev.month + 1 };
    });
    setSelectedDate(null);
  };

  const handleComplete = async (appt: Appointment) => {
    const notes = prompt('Completion notes (optional):');
    if (notes === null) return;
    try {
      await bookingAPI.completeAppointment(appt.id, { notes });
      fetchCalendar();
      setEditingAppt(null);
    } catch (err) {
      alert('Failed to mark as completed');
    }
  };

  const handleNoShow = async (appt: Appointment) => {
    if (!window.confirm(`Mark ${appt.booker_name} as no-show?`)) return;
    try {
      await bookingAPI.noShowAppointment(appt.id);
      fetchCalendar();
      setEditingAppt(null);
    } catch (err) {
      alert('Failed to mark as no-show');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingAppt) return;
    setSaving(true);
    try {
      await bookingAPI.updateAppointment(editingAppt.id, {
        notes: editNotes,
        video_link: editVideoLink,
      });
      fetchCalendar();
      setEditingAppt(null);
    } catch (err) {
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (appt: Appointment) => {
    setEditingAppt(appt);
    setEditNotes(appt.notes);
    setEditVideoLink(appt.video_link);
  };

  // Build calendar grid
  const { year, month } = currentMonth;
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const startPad = firstDay.getDay();
  const totalDays = lastDay.getDate();
  const todayStr = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;

  // Group appointments by date
  const apptsByDate: Record<string, Appointment[]> = {};
  appointments.forEach(a => {
    if (!apptsByDate[a.date]) apptsByDate[a.date] = [];
    apptsByDate[a.date].push(a);
  });

  const blockoutDates = new Set(blockouts.map(b => b.date));

  // Today's upcoming appointments
  const todayAppts = (apptsByDate[todayStr] || [])
    .filter(a => a.status === 'confirmed')
    .sort((a, b) => a.start_time.localeCompare(b.start_time));

  // Selected date appointments
  const selectedAppts = selectedDate ? (apptsByDate[selectedDate] || []) : [];

  return (
    <div className="crm-page">
      <CRMSubNav />
      <div className="crm-header">
        <h1>Booking Calendar</h1>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Link to="/crm/booking-settings" className="crm-btn crm-btn-secondary">
            Settings
          </Link>
        </div>
      </div>

      <div className="crm-calendar-layout">
        {/* Sidebar */}
        <div className="crm-calendar-sidebar">
          <div className="crm-card">
            <h3 style={{ margin: '0 0 1rem 0' }}>Today's Meetings</h3>
            {todayAppts.length === 0 ? (
              <p style={{ color: '#999', margin: 0, fontSize: '0.9rem' }}>No meetings today</p>
            ) : (
              todayAppts.map(appt => (
                <div key={appt.id} className="crm-calendar-sidebar-appt">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>{formatTime12(appt.start_time)}</strong>
                    <span
                      className="crm-calendar-chip"
                      style={{ backgroundColor: appt.meeting_type.color, color: '#fff' }}
                    >
                      {appt.meeting_type.name}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#333', marginTop: '0.25rem' }}>
                    {appt.booker_name}
                    {appt.customer_name && <span style={{ color: '#999' }}> ({appt.customer_name})</span>}
                  </div>
                  {appt.video_link && (
                    <a
                      href={appt.video_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="crm-calendar-join-btn"
                    >
                      Join Call
                    </a>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="crm-card" style={{ marginTop: '1rem' }}>
            <h3 style={{ margin: '0 0 0.75rem 0' }}>Quick Actions</h3>
            <Link to="/crm/booking-settings" className="crm-calendar-action-link">
              Edit Availability
            </Link>
            <button
              className="crm-calendar-action-link"
              onClick={() => {
                const url = `${window.location.origin}/book/portal-walkthrough`;
                navigator.clipboard.writeText(url).then(() => alert('Booking link copied!'));
              }}
            >
              Copy Booking Link
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="crm-calendar-main">
          <div className="crm-card">
            <div className="crm-calendar-nav">
              <button onClick={handlePrevMonth} className="crm-btn crm-btn-sm">&larr;</button>
              <h2 style={{ margin: 0 }}>{MONTH_NAMES[month - 1]} {year}</h2>
              <button onClick={handleNextMonth} className="crm-btn crm-btn-sm">&rarr;</button>
            </div>

            {loading ? (
              <p style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>Loading...</p>
            ) : (
              <div className="crm-calendar-grid">
                {DAY_HEADERS.map(d => (
                  <div key={d} className="crm-calendar-hdr">{d}</div>
                ))}

                {Array.from({ length: startPad }, (_, i) => (
                  <div key={`pad-${i}`} className="crm-calendar-cell empty" />
                ))}

                {Array.from({ length: totalDays }, (_, i) => {
                  const d = i + 1;
                  const dateStr = `${year}-${month.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
                  const dayAppts = apptsByDate[dateStr] || [];
                  const hasBlockout = blockoutDates.has(dateStr);
                  const isToday = dateStr === todayStr;
                  const isSelected = dateStr === selectedDate;

                  return (
                    <div
                      key={dateStr}
                      className={`crm-calendar-cell ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${hasBlockout ? 'blocked' : ''}`}
                      onClick={() => setSelectedDate(dateStr)}
                    >
                      <div className="crm-calendar-day-num">{d}</div>
                      <div className="crm-calendar-cell-appts">
                        {dayAppts.slice(0, 3).map(a => (
                          <div
                            key={a.id}
                            className="crm-calendar-cell-chip"
                            style={{
                              backgroundColor: a.status === 'cancelled' ? '#eee' : a.meeting_type.color,
                              color: a.status === 'cancelled' ? '#999' : '#fff',
                              textDecoration: a.status === 'cancelled' ? 'line-through' : 'none',
                            }}
                            title={`${formatTime12(a.start_time)} - ${a.booker_name}`}
                          >
                            {formatTime12(a.start_time).replace(/ (AM|PM)/, '')} {a.booker_name.split(' ')[0]}
                          </div>
                        ))}
                        {dayAppts.length > 3 && (
                          <div className="crm-calendar-cell-more">+{dayAppts.length - 3} more</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Day detail panel */}
          {selectedDate && (
            <div className="crm-card" style={{ marginTop: '1rem' }}>
              <h3 style={{ margin: '0 0 1rem 0' }}>
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                  weekday: 'long', month: 'long', day: 'numeric'
                })}
              </h3>

              {selectedAppts.length === 0 ? (
                <p style={{ color: '#999', margin: 0 }}>No appointments on this day</p>
              ) : (
                selectedAppts.map(appt => (
                  <div key={appt.id} className="crm-calendar-detail-appt">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <strong>{formatTime12(appt.start_time)} - {formatTime12(appt.end_time)}</strong>
                        <span
                          className="crm-calendar-chip"
                          style={{
                            backgroundColor: appt.meeting_type.color,
                            color: '#fff',
                            marginLeft: '0.5rem',
                          }}
                        >
                          {appt.meeting_type.name}
                        </span>
                        <span
                          className="crm-calendar-chip"
                          style={{
                            backgroundColor: STATUS_COLORS[appt.status] || '#999',
                            color: '#fff',
                            marginLeft: '0.25rem',
                          }}
                        >
                          {appt.status}
                        </span>
                      </div>
                      {appt.status === 'confirmed' && (
                        <button onClick={() => openEdit(appt)} className="crm-btn crm-btn-sm">
                          Edit
                        </button>
                      )}
                    </div>
                    <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                      <div><strong>{appt.booker_name}</strong> &mdash; {appt.booker_email}</div>
                      {appt.booker_phone && <div>Phone: {appt.booker_phone}</div>}
                      {appt.customer_name && <div>Customer: {appt.customer_name}</div>}
                      {appt.notes && <div style={{ color: '#666', marginTop: '0.25rem' }}>{appt.notes}</div>}
                      {appt.video_link && (
                        <a href={appt.video_link} target="_blank" rel="noopener noreferrer" style={{ color: '#2D5A27' }}>
                          Video Link
                        </a>
                      )}
                    </div>

                    {appt.status === 'confirmed' && (
                      <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                        <button onClick={() => handleComplete(appt)} className="crm-btn crm-btn-success crm-btn-sm">
                          Complete
                        </button>
                        <button onClick={() => handleNoShow(appt)} className="crm-btn crm-btn-danger crm-btn-sm">
                          No Show
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingAppt && (
        <div className="crm-modal-overlay" onClick={() => setEditingAppt(null)}>
          <div className="crm-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Edit Appointment</h3>
            <div style={{ marginBottom: '0.75rem' }}>
              <strong>{editingAppt.booker_name}</strong> &mdash; {editingAppt.meeting_type.name}
              <br />
              {new Date(editingAppt.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              {' '}at {formatTime12(editingAppt.start_time)}
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>Video Link</label>
              <input
                type="url"
                value={editVideoLink}
                onChange={e => setEditVideoLink(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                placeholder="https://meet.google.com/..."
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>Notes</label>
              <textarea
                value={editNotes}
                onChange={e => setEditNotes(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', minHeight: '80px', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setEditingAppt(null)} className="crm-btn crm-btn-secondary">Cancel</button>
              <button onClick={handleSaveEdit} className="crm-btn crm-btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMCalendar;
